import * as React from "react";

import { Wrapper, Spacer, Bubble, BubbleWrapper, UserName, SelectionCircle } from "./chat-bubble-style";
import UserCircleImage from "../../../views/user-circle-image/user-circle-image";
import { getFirstNameOfUsername, assertNever } from "common/Support";
import { tokenizeMessage } from "../../../../lib/message-tokenizer";
import Img from "../../img/img";
import { Check } from "common/react-plucked";

export type BubbleStyle = "normal" | "selected" | "unselected";

interface Props {
    orientation: "left" | "right";
    text: string;
    userEmail: string;
    useAccent: boolean;
    userName: string;
    image?: string;
    token: string;
    isFromSameAuthor: boolean;
    bubbleStyle: BubbleStyle;

    onLongPress: (token: string) => void;
    onSelected: (token: string) => void;
}

interface State {}

export default class ChatBubble extends React.PureComponent<Props, State> {
    private readonly USER_CIRCLE_SIZE = 32;
    public state: State = {};

    private wrapperRef = React.createRef<any>();

    public componentDidMount(): void {
        this.wrapperRef.current.addEventListener("long-press", this.onLongPress);
    }

    public componentWillUnmount(): void {
        this.wrapperRef.current.removeEventListener("long-press", this.onLongPress);
    }

    private onLongPress = (_e: CustomEvent) => {
        this.props.onLongPress(this.props.token);
    };

    private onSelectionClicked = () => {
        this.props.onSelected(this.props.token);
    };

    public render(): React.ReactNode {
        const { orientation, text, userName, userEmail, isFromSameAuthor, bubbleStyle: style, image } = this.props;

        let circleImage: React.ReactNode;
        if (style === "normal") {
            if (!isFromSameAuthor) {
                circleImage = (
                    <UserCircleImage image={image} email={userEmail} username={userName} size={this.USER_CIRCLE_SIZE} />
                );
            }
        } else {
            circleImage = (
                <SelectionCircle selected={this.props.bubbleStyle === "selected"} onClick={this.onSelectionClicked}>
                    {this.props.bubbleStyle === "selected" && <Check size={16} />}
                </SelectionCircle>
            );
        }

        const tokens = tokenizeMessage(text);

        return (
            <Wrapper
                data-test="app-chat-bubble"
                ref={this.wrapperRef}
                orientation={orientation}
                useBottomMargin={isFromSameAuthor}
                isFromSameAuthor={isFromSameAuthor && style === "normal"}
                userCircleSize={this.USER_CIRCLE_SIZE}>
                <Spacer />
                <BubbleWrapper orientation={orientation}>
                    <Bubble orientation={orientation}>
                        {
                            // eslint-disable-next-line
                            tokens.map((token, index) => {
                                const type = token.type;
                                switch (type) {
                                    case "text":
                                    case "raw":
                                        return <span key={index}>{token.data}</span>;
                                    case "newline":
                                        return <br key={index} />;
                                    case "url":
                                        return (
                                            <Img
                                                key={index}
                                                src={token.data}
                                                altBehavior="both"
                                                alternate={
                                                    <a key={index} href={token.data}>
                                                        {token.data}
                                                    </a>
                                                }
                                            />
                                        );
                                    default:
                                        assertNever(type);
                                }
                            })
                        }
                    </Bubble>
                    {!isFromSameAuthor && <UserName>{getFirstNameOfUsername(userName)}</UserName>}
                </BubbleWrapper>
                {circleImage}
            </Wrapper>
        );
    }
}
